import { useCallback, useMemo, useState } from 'react';

import {
  buildScheduleEndTimeOptions,
  buildScheduleStartTimeOptions,
  getOccupiedHourRangesForDay,
  isDayOfWeek,
} from './utils';

import type { ChildScheduleSlot, DayOfWeek, IChildSchedule } from '../../../types';
import type { SelectValueType } from '../../../../../../utils/types';

export default function useScheduleSlotForm(schedules: ReadonlyArray<IChildSchedule>) {
  const [dayOfWeek, setDayOfWeek] = useState<DayOfWeek | ''>('');
  const [activityType, setActivityType] = useState('');
  const [startHour, setStartHour] = useState<number | ''>('');
  const [endHour, setEndHour] = useState<number | ''>('');

  const occupiedHourRanges = useMemo(
    () => (dayOfWeek === '' ? [] : getOccupiedHourRangesForDay(schedules, dayOfWeek)),
    [schedules, dayOfWeek],
  );
  const startTimeOptions = useMemo(
    () => buildScheduleStartTimeOptions(occupiedHourRanges),
    [occupiedHourRanges],
  );
  const endTimeOptions = useMemo(
    () => buildScheduleEndTimeOptions(occupiedHourRanges, startHour),
    [occupiedHourRanges, startHour],
  );

  let slot: ChildScheduleSlot | null = null;
  if (dayOfWeek !== '' && activityType !== '' && startHour !== '' && endHour !== '') {
    slot = { dayOfWeek, activityType, startHour, endHour };
  }

  const handleDayChange = useCallback((value: SelectValueType) => {
    setDayOfWeek(isDayOfWeek(value) ? value : '');
    setStartHour('');
    setEndHour('');
  }, []);

  const handleActivityChange = useCallback((value: SelectValueType) => {
    setActivityType(String(value));
  }, []);

  const handleStartTimeChange = useCallback((value: SelectValueType) => {
    setStartHour(Number(value));
    setEndHour('');
  }, []);

  const handleEndTimeChange = useCallback((value: SelectValueType) => {
    setEndHour(Number(value));
  }, []);

  const reset = useCallback(() => {
    setDayOfWeek('');
    setActivityType('');
    setStartHour('');
    setEndHour('');
  }, []);

  return {
    dayOfWeek,
    activityType,
    startHour,
    endHour,
    startTimeOptions,
    endTimeOptions,
    slot,
    handleDayChange,
    handleActivityChange,
    handleStartTimeChange,
    handleEndTimeChange,
    reset,
  };
}
